import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, canManageUsers, type AuthUser } from '@/lib/api';

export default function AllianceSettings({ user }: { user: AuthUser }) {
  const qc = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['alliance-settings'],
    queryFn: api.getAllianceSettings,
  });
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [slug, setSlug] = useState('');
  const [motto, setMotto] = useState('');
  const [lastrankUrl, setLastrankUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const data = settingsQuery.data;
  useEffect(() => {
    if (!data) return;
    setName(data.name);
    setTag(data.tag ?? '');
    setSlug(data.slug ?? '');
    setMotto(data.motto ?? '');
    setLastrankUrl(data.lastrankUrl ?? '');
  }, [data]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['alliance-settings'] });
    qc.invalidateQueries({ queryKey: ['me'] });
  };
  const onError = (e: unknown) => setError(e instanceof ApiError ? e.message : 'Failed');

  const save = useMutation({
    mutationFn: () =>
      api.updateAllianceSettings({
        name: name.trim(),
        tag: tag.trim() || null,
        slug: slug.trim(),
        motto: motto.trim() || null,
        lastrankUrl: lastrankUrl.trim() || null,
      }),
    onSuccess: () => {
      refresh();
      setError(null);
      setSaved(true);
    },
    onError,
  });
  const upload = useMutation({
    mutationFn: (file: File) => api.uploadAllianceLogo(file),
    onSuccess: () => {
      refresh();
      setError(null);
    },
    onError,
  });
  const removeLogo = useMutation({
    mutationFn: api.deleteAllianceLogo,
    onSuccess: refresh,
    onError,
  });

  if (!canManageUsers(user.role)) {
    return <div className="text-slate-400">Only R5 can edit the alliance settings.</div>;
  }
  if (!data) return <div className="text-slate-400">Loading…</div>;

  const publicBoardUrl = `${window.location.origin}/public/${data.slug ?? ''}/board`;

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold">Alliance settings</h1>
        <p className="text-sm text-slate-400">
          Name and logo are shown on the public board, public albums and the player hub.
        </p>
      </header>

      <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">Logo</h3>
        <div className="flex items-center gap-4">
          {data.logoUrl ? (
            <img src={data.logoUrl} alt="" className="h-20 w-20 rounded object-contain" />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded border border-dashed border-slate-700 text-xs text-slate-500">
              No logo
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
                e.target.value = '';
              }}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={upload.isPending}
              className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {upload.isPending ? 'Uploading…' : data.logoUrl ? 'Replace logo' : 'Upload logo'}
            </button>
            {data.logoUrl && (
              <button
                onClick={() => removeLogo.mutate()}
                disabled={removeLogo.isPending}
                className="rounded border border-red-900 px-3 py-1.5 text-sm text-red-300 hover:bg-red-900/40 disabled:opacity-40"
              >
                Remove
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-500">PNG, JPEG or WebP, max 5 MB. Square works best.</p>
      </section>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSaved(false);
          save.mutate();
        }}
        className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4"
      >
        <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">General</h3>
        <Field label="Alliance name">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="Tag">
          <input
            className="input"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            maxLength={16}
            placeholder="e.g. FoA"
          />
        </Field>
        <Field label="Slogan" hint="Shown under the name on public pages and plan exports.">
          <input
            className="input"
            value={motto}
            onChange={(e) => setMotto(e.target.value)}
            maxLength={120}
            placeholder="e.g. One Team. One Fight. One Victory."
          />
        </Field>
        <Field label="Public URL slug" hint={publicBoardUrl}>
          <input
            className="input"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            pattern="[a-z0-9](?:[a-z0-9\-]{0,30}[a-z0-9])?"
            required
          />
        </Field>
        <Field label="LastRank alliance URL" hint="Source for the roster sync.">
          <input
            className="input"
            type="url"
            value={lastrankUrl}
            onChange={(e) => setLastrankUrl(e.target.value)}
            placeholder="https://lastrank.fun/…"
          />
        </Field>
        <div className="flex items-center gap-3">
          <button
            className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-500 disabled:opacity-50"
            disabled={save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          {saved && !save.isPending && <span className="text-sm text-emerald-400">Saved.</span>}
        </div>
      </form>
      {error && <div className="text-sm text-red-400">{error}</div>}

      <style>{`
        .input {
          background: rgb(2 6 23 / 1);
          border: 1px solid rgb(51 65 85 / 1);
          border-radius: 6px;
          padding: 6px 10px;
          font-size: 14px;
          color: inherit;
          width: 100%;
        }
        .input:focus { outline: none; border-color: rgb(100 116 139 / 1); }
      `}</style>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-sm text-slate-300">{label}</span>
      {children}
      {hint && <span className="block break-all text-xs text-slate-500">{hint}</span>}
    </label>
  );
}
